import { Navigate } from "react-router-dom";
import { Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useTenant } from "hooks/useTenant";
import { useCurrentUser } from "hooks/useCurrentUser";
import { SpinnerOverlay } from "components/SpinnerOverlay";
import { Home } from "routes";
import { useFeatureFlags } from "hooks/useFeatureFlags";

/**
 * This component is used to redirect the user to the correct page based on their permissions.
 * So that we never just send a user to a blank page.
 *
 * Our preferred order for redirection from root is:
 * 1. Chemicals Overview
 * 2. Chemicals Reports
 * 3. Waste Overview
 * 4. Insights
 * 5. Facilities
 * 6. Contacts
 * 7. Documents
 * 8. Credentials
 * 9. Generic Home
 */
export function CustomerRootRedirect() {
  const { hasPermissions } = useAuthorization();
  const { tenant, loading: loadingTenant } = useTenant();
  const { loading: loadingUser } = useCurrentUser();
  const { featureFlags, loading: loadingFeatureFlags } = useFeatureFlags();

  // Wait for both tenant and user data to be loaded before making permission checks
  if (loadingTenant || loadingUser || loadingFeatureFlags) {
    return <SpinnerOverlay loading />;
  }

  if (!tenant?.id) {
    return <Navigate to="/" replace />;
  }

  if (featureFlags?.["chemicals-overview"]) {
    return <Navigate to={`/o/${tenant.id}/chemicals/overview`} replace />;
  }

  if (hasPermissions([Permission.RouteEpcraReports])) {
    return <Navigate to={`/o/${tenant.id}/chemicals/reports`} replace />;
  }

  if (featureFlags?.["waste-overview"]) {
    return <Navigate to={`/o/${tenant.id}/waste/overview`} replace />;
  }

  if (hasPermissions([Permission.RouteInsights])) {
    return <Navigate to={`/o/${tenant.id}/insights/overview`} replace />;
  }

  // Priority order for redirects based on permissions
  // This follows the same logic as the navigation items in CustomerLayout
  if (hasPermissions([Permission.RouteFacilities])) {
    return <Navigate to={`/o/${tenant.id}/facilities`} replace />;
  }

  if (hasPermissions([Permission.RouteContacts])) {
    return <Navigate to={`/o/${tenant.id}/contacts`} replace />;
  }

  if (hasPermissions([Permission.RouteDocuments])) {
    return <Navigate to={`/o/${tenant.id}/documents`} replace />;
  }

  if (hasPermissions([Permission.RouteEpcraCredentials])) {
    return <Navigate to={`/o/${tenant.id}/credentials`} replace />;
  }

  // If no specific permissions, redirect to a safe fallback
  // This could be a "no access" page or back to home
  return <Home name={"Hero"} />;
}
